import React, { useEffect, useRef, useState } from 'react';
import './style.scss';
import IconButton from '../IconButton/index.js';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { createAction } from '../../../utils/sagaHelper.js';
import { useDispatch } from 'react-redux';
import { getHistoryReportById } from '../../../api/reportsHistory-request.js';
import { isEmptyObject, objectKeysToUpperLowerCase } from '../../../utils/objects-util.js';
import { REPORTS_HISTORY_DOWNLOAD } from '../../../store/reducers/reportHistory/report-history-actions.js';
import { useOnClickOutside } from '../../../services/hooks.js';
import { createPortal } from 'react-dom';
import { isRTL } from '../../../services/userModel.js';
import FullScreenConfirmDialog from '../FullScreenConfirmDialog/FullScreenConfirmDialog.jsx';
import { ReportAction, ReportFormat } from '../../../utils/enums.js';
import moment from 'moment';
import { DATE_FORMAT_DMY2_HM } from '../../../utils/convertTime.js';
import { toast } from 'react-toastify';

const reportStatus = {
  idle: 0,
  loading: 1,
  done: 2,
}

const reportFileStatus = {
  acknowledged: 0,
  completed: 1,
  failed: 2
}

const controlConfig = {
  [reportStatus.idle]: {
    icon: 'reportIcon',
    text: 'report-download.SAVE',
  },
  [reportStatus.loading]: {
    icon: 'reportLoading',
    text: 'report-download.SAVING',
  },
  [reportStatus.done]: {
    icon: 'reportDone',
    text: 'report-download.READY',
  },
}

const statusClassName = {
  [reportStatus.idle]: 'idle',
  [reportStatus.loading]: 'loading',
  [reportStatus.done]: 'done',
}

const ExportFileControl = ({ horizontal, hideText, model, control, ghostButton, FilterForm, filterFormProps, portalClassName, canSavePdfReport, hideExelReport }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [isShowModal, setShowModal] = useState(false);
  const [status, setStatus] = useState(reportStatus.idle);
  const [file, setFile] = useState(null);
  const interval = useRef(null);
  const exelButtonRef = useRef();
  const [reportId, setReportId] = useState(null);
  const [savedReportFormat, setSavedReportFormat] = useState(null);

  const [showRegeneratePopup, setShowRegeneratePopup] = useState();

  const targetRef = useRef();
  useOnClickOutside(targetRef, () => {
    setShowModal(false);
  }, exelButtonRef);

  const config = controlConfig[status];

  const saveFile = (repotId) => {
    dispatch(createAction(REPORTS_HISTORY_DOWNLOAD, repotId));
  }

  useEffect(() => {
    return () => {
      if (interval.current && reportId) {
        clearInterval(interval.current);
        runInterval(reportId, 15);
      }
    }
  // eslint-disable-next-line
  }, []);

  const runInterval = (reportId, intervalTimeInSec = 15) => {
    interval.current = setInterval(async () => {
      const response = await getHistoryReportById(reportId);
      const updatedFile = objectKeysToUpperLowerCase(response.data);
      if (updatedFile === null || !updatedFile) {
        setStatus(reportStatus.idle);
        clearInterval(interval.current);
        return;
      }
      if (updatedFile.status === reportFileStatus.failed) {
        setStatus(reportStatus.idle);
        clearInterval(interval.current);
        setReportId(null);
        toast.error(t('report-download.FAILED'));
        return;
      }
      if (updatedFile && updatedFile.fileReady) {
        onFileReady(updatedFile);
        clearInterval(interval.current);
      }
    }, intervalTimeInSec * 1000);
  }

  const addToQueue = (filterProps, reportAction, reportFormat) => {
    setStatus(reportStatus.loading);
    const format = reportFormat ?? savedReportFormat;
    dispatch(createAction(control.action, {
      model: filterProps || model,
      reportType: format === ReportFormat.Pdf ? (control.pdfReportType ?? control.reportType) : control.reportType,
      reportAction: reportAction ?? ReportAction.Get,
      reportId: reportAction === ReportAction.Regenerate && file ? file?.reportId : null,
      reportFormat: format,
    })).then(data => {
      if (data.status === reportFileStatus.failed) {
        setStatus(reportStatus.idle);
        toast.error(t('report-download.FAILED'));
        return;
      }
      if (isEmptyObject(data)) {
        setStatus(reportStatus.idle);
        return;
      }
      if (data.reportId && !data.fileReady) {
        setFile(data);
        runInterval(data.reportId, 1);
        setReportId(data.reportId);
      }
      if (data.fileReady) {
        onFileReady(data, true);
      }
    }).catch(err => {
      clearInterval(interval.current);
      setStatus(reportStatus.idle);
    });
  }

  const onFileReady = (file, saveIfIpdated) => {
    setStatus(reportStatus.done);
    setFile(file);

    if (file.fileReady && saveIfIpdated) {
      setShowRegeneratePopup(true);
      return;
    }
    saveFile(file.reportId);
  }

  const onDownload = (reportFormat) => {
    if (status === reportStatus.loading) {
      return;
    }

    setSavedReportFormat(reportFormat);

    if (control.actionFn) {
      control.actionFn();
      return;
    }

    addToQueue(null, null, reportFormat);
  }

  const handleOnRegenerate = () => {
    setShowRegeneratePopup(false);
    addToQueue(null, ReportAction.Regenerate);
  }

  const onSave = () => {
    setShowRegeneratePopup(false);
    saveFile(file.reportId);
  }

  const onClose = () => {
    setShowRegeneratePopup(false);
  }

  const rect = exelButtonRef?.current?.getBoundingClientRect();
  const top = rect?.top || 0;
  const left = (rect?.left + (isRTL() ? rect?.width + 10 : 0)) || 0;
  const right = (rect?.right - (isRTL() ? rect?.width : 0)) || 0;
  const bottom = rect?.bottom || 0;
  const inDangerouseZone = rect ? window.innerHeight - rect?.y < 200 : false;
  const blockHeight = 36 + (hideExelReport ? 0 : 36) + (canSavePdfReport ? 36 : 0) + (status === reportStatus.done ? 36 : 0);

  const style = {
    position: "absolute",
    zIndex: 5,
    left: horizontal ? left + window.scrollX : right + window.scrollX,
    ...(inDangerouseZone
    ? {
        top: top + window.scrollY - blockHeight - (horizontal ? -10 : 42),
      }
    : {
        top: horizontal ? top + window.scrollY : bottom + window.scrollY,
      })
  };

  return (
    <div className="exportFileControl" ref={exelButtonRef}>
      <div className={classnames("downloadControl button", statusClassName[status], { ghostButton })} onClick={() => setShowModal(!isShowModal)}>
        <IconButton isIcon iconName={config.icon} />
        {!hideText && (
          <span className={"downloadText"}>{t(config.text)}</span>
        )}
      </div>

      {isShowModal && createPortal((
        <div style={style} className={classnames("exportFileModalPortal", portalClassName, { rtl: isRTL() })} ref={targetRef}>
          <div className={classnames("exportFileModal", { show: isShowModal, horizontal, rtl: isRTL() })}>
            <div onClick={() => history.push("/reports-history")} className={'button'}>{t('report-download.SEE_ALL_REPORT')}</div>
            {!hideExelReport && (
              <div className={classnames('button', { disable: status === reportStatus.loading })} onClick={() => onDownload(ReportFormat.Exel)}>{t('report-download.DOWNLOAD_EXEL')}</div>
            )}
            {!!canSavePdfReport && (
              <div className={classnames('button', { disable: status === reportStatus.loading })} onClick={() => onDownload(ReportFormat.Pdf)}>{t('report-download.DOWNLOAD_PDF')}</div>
            )}
            {status === reportStatus.done && (
              <div className={'button done'} onClick={onSave}>{t('report-download.SAVE')}</div>
            )}
          </div>
        </div>
      ), document.body)}

      {showRegeneratePopup && !!file && (
        <FullScreenConfirmDialog
          showCloseButton
          handleOnClose={onClose}
          useWrapperClass={'exportFileConfirmSave'}
          t={t}
          description={t('report-download.FILE_ALREADY_CREATED', { date: moment(file?.updated || file?.created).format(DATE_FORMAT_DMY2_HM) })}
          handleOnConfirm={onSave}
          confirmText={t('report-download.SAVE')}
          handleOnCancel={handleOnRegenerate}
          cancelText={t('report-download.REFRESH')}
        />
      )}

      {FilterForm && <FilterForm {...filterFormProps} onSave={addToQueue} />}
    </div>
  )
}

export default ExportFileControl;